/**
 * Immediate Flight Status Service
 * Provides instant flight status data when creating trips to avoid waiting for backend processing
 */

import { FLIGHT_AWARE_API_BASE } from '../config/environment.js';
import { logger } from '../utils/logger';
import { authFetch } from '../utils/authFetch';

/**
 * Check whether a flight is within the queryable tracking window.
 * Returns null if queryable, or a reason string if not.
 * @param {Object} flight
 * @returns {string|null}
 */
const checkTrackingWindow = (flight) => {
  const now = Date.now();
  const departureTime = new Date(flight.departureTime).getTime();
  const hoursUntilFlight = (departureTime - now) / (1000 * 60 * 60);
  const hoursSinceFlight = (now - departureTime) / (1000 * 60 * 60);

  if (hoursUntilFlight > 48) {
    logger.debug('[ImmediateFlightStatus] Flight is too far in the future (>48h), skipping API call:', {
      hoursUntilFlight: Math.round(hoursUntilFlight),
      departureTime: new Date(departureTime).toISOString()
    });
    return 'too_far_future';
  }

  if (hoursSinceFlight > 24) {
    logger.debug('[ImmediateFlightStatus] Flight landed >24h ago, skipping API call:', {
      hoursSinceFlight: Math.round(hoursSinceFlight)
    });
    return 'too_far_past';
  }

  return null;
};

/**
 * Build the date range and API URL for a flight query.
 * @param {Object} flight
 * @returns {{ url: string, flightIdent: string }}
 */
const buildFlightQueryUrl = (flight) => {
  const flightIdent = `${flight.airline}${flight.flightNumber}`;
  const departureDate = new Date(flight.departureTime);
  const year = departureDate.getFullYear();
  const month = String(departureDate.getMonth() + 1).padStart(2, '0');
  const day = String(departureDate.getDate()).padStart(2, '0');
  const startDate = `${year}-${month}-${day}`;

  const endDateObj = new Date(departureDate);
  endDateObj.setDate(endDateObj.getDate() + 2);
  const endYear = endDateObj.getFullYear();
  const endMonth = String(endDateObj.getMonth() + 1).padStart(2, '0');
  const endDay = String(endDateObj.getDate()).padStart(2, '0');
  const endDate = `${endYear}-${endMonth}-${endDay}`;

  const url = `${FLIGHT_AWARE_API_BASE}/${encodeURIComponent(flightIdent)}?start=${startDate}&end=${endDate}`;
  return { url, flightIdent };
};

/**
 * Select the best matching flight from API results by route, preferring upcoming flights.
 * @param {Array} flights - Raw flights from API
 * @param {string} departureAirport
 * @param {string} arrivalAirport
 * @returns {Object|null}
 */
const selectMatchingFlight = (flights, departureAirport, arrivalAirport) => {
  const matchingFlights = flights.filter(f =>
    f.origin?.code_iata === departureAirport &&
    f.destination?.code_iata === arrivalAirport
  );

  logger.debug('[ImmediateFlightStatus] Matching flights:', {
    total: flights.length,
    matching: matchingFlights.length,
    routes: flights.map(f => `${f.origin?.code_iata} → ${f.destination?.code_iata}`)
  });

  if (matchingFlights.length === 0) {
    console.warn('[ImmediateFlightStatus] No flights match the route');
    return null;
  }

  const currentTime = new Date();
  const upcomingFlights = matchingFlights.filter(f => {
    const scheduledTime = new Date(f.scheduled_out || f.scheduled_off);
    return scheduledTime > currentTime && !f.status?.toLowerCase().includes('arrived');
  });

  return upcomingFlights.length > 0 ? upcomingFlights[0] : matchingFlights[0];
};

/**
 * Build the departure sub-object from a raw FlightAware flight.
 */
const buildDepartureStatus = (f) => ({
  scheduled: f.scheduled_off || f.scheduled_out,
  estimated: f.estimated_off || f.estimated_out,
  actual: f.actual_off || f.actual_out,
  gate: f.gate_origin,
  terminal: f.terminal_origin,
  runway: f.actual_runway_off,
  delay: calculateDelay(f.scheduled_off || f.scheduled_out, f.estimated_off || f.estimated_out)
});

/**
 * Build the arrival sub-object from a raw FlightAware flight.
 */
const buildArrivalStatus = (f) => ({
  scheduled: f.scheduled_on || f.scheduled_in,
  estimated: f.estimated_on || f.estimated_in,
  actual: f.actual_on || f.actual_in,
  gate: f.gate_destination,
  terminal: f.terminal_destination,
  runway: f.actual_runway_on,
  baggageClaim: f.baggage_claim,
  delay: calculateDelay(f.scheduled_on || f.scheduled_in, f.estimated_on || f.estimated_in)
});

/**
 * Convert a raw FlightAware flight object into our internal status format.
 * @param {Object} matchedFlight - Raw flight from FlightAware
 * @returns {Object}
 */
const buildFlightStatusResult = (matchedFlight) => {
  return {
    status: matchedFlight.status || 'Unknown',
    lastUpdated: new Date().toISOString(),
    source: 'immediate-lookup',
    departure: buildDepartureStatus(matchedFlight),
    arrival: buildArrivalStatus(matchedFlight),
    aircraft: {
      type: matchedFlight.aircraft_type,
      registration: matchedFlight.registration
    },
    progress: matchedFlight.progress_percent || 0,
    delays: {
      departure: matchedFlight.departure_delay || 0,
      arrival: matchedFlight.arrival_delay || 0
    },
    isCancelled: matchedFlight.cancelled || false,
    isDiverted: matchedFlight.diverted || false
  };
};

/**
 * Get immediate flight status data for a flight
 * @param {Object} flight - Flight object with airline, flightNumber, departureAirport, arrivalAirport, departureTime
 * @returns {Promise<Object|null>} Flight status data or null if not available
 */
export const getImmediateFlightStatus = async (flight) => {
  try {
    logger.debug('[ImmediateFlightStatus] Fetching flight data for:', {
      airline: flight.airline,
      flightNumber: flight.flightNumber,
      route: `${flight.departureAirport} → ${flight.arrivalAirport}`,
      departureTime: flight.departureTime
    });

    if (!flight.airline || !flight.flightNumber || !flight.departureAirport || !flight.arrivalAirport) {
      console.warn('[ImmediateFlightStatus] Missing required flight fields:', flight);
      return null;
    }

    const windowCheck = checkTrackingWindow(flight);
    if (windowCheck) {
      return null;
    }

    const { url, flightIdent } = buildFlightQueryUrl(flight);
    logger.debug('[ImmediateFlightStatus] API URL:', url);

    const response = await authFetch(url);

    if (!response.ok) {
      console.error('[ImmediateFlightStatus] API error:', response.status, response.statusText);
      const errorText = await response.text();
      console.error('[ImmediateFlightStatus] Error response:', errorText);
      return null;
    }

    const data = await response.json();
    logger.debug('[ImmediateFlightStatus] API response:', {
      flightCount: data.flights?.length || 0,
      hasFlights: !!data.flights
    });

    if (!data.flights || data.flights.length === 0) {
      console.warn('[ImmediateFlightStatus] No flights returned from API');
      return null;
    }

    const matchedFlight = selectMatchingFlight(data.flights, flight.departureAirport, flight.arrivalAirport);
    if (!matchedFlight) {
      return null;
    }

    const result = buildFlightStatusResult(matchedFlight);

    logger.debug('[ImmediateFlightStatus] Successfully fetched flight status:', {
      flightIdent,
      status: result.status,
      hasData: true
    });

    return result;

  } catch (error) {
    console.error('[ImmediateFlightStatus] Unexpected error:', error);
    return null;
  }
};

/**
 * Get immediate flight status for multiple flights
 * @param {Array} flights - Array of flight objects
 * @returns {Promise<Object>} Object with flightId as keys and status data as values
 */
export const getImmediateFlightStatuses = async (flights) => {
  const flightStatuses = {};
  
  // Process flights in parallel for better performance
  const promises = flights.map(async (flight) => {
    if (!flight.flightId) return;
    
    const status = await getImmediateFlightStatus(flight);
    if (status) {
      flightStatuses[flight.flightId] = status;
    }
  });

  await Promise.all(promises);
  
  return flightStatuses;
};

/**
 * Helper function to calculate delay in minutes
 * @param {string} scheduled - Scheduled time
 * @param {string} estimated - Estimated time
 * @returns {number} Delay in minutes
 */
const calculateDelay = (scheduled, estimated) => {
  if (!scheduled || !estimated) return 0;
  
  const scheduledTime = new Date(scheduled).getTime();
  const estimatedTime = new Date(estimated).getTime();
  const delayMs = estimatedTime - scheduledTime;
  
  return Math.round(delayMs / (1000 * 60)); // Convert to minutes
};

/**
 * Enhance trip data with immediate flight status
 * @param {Object} tripData - Trip data object
 * @returns {Promise<Object>} Enhanced trip data with flight statuses
 */
export const enhanceTripWithImmediateFlightStatus = async (tripData) => {
  if (!tripData.flights || tripData.flights.length === 0) {
    return tripData;
  }

  try {
    const flightStatuses = await getImmediateFlightStatuses(tripData.flights);
    
    if (Object.keys(flightStatuses).length > 0) {
      return {
        ...tripData,
        flightStatuses,
        flightStatusesLastChecked: new Date().toISOString()
      };
    }
    
    return tripData;
  } catch (error) {
    console.error('Error enhancing trip with flight status:', error);
    return tripData;
  }
};

